import { Link, useLocation, useNavigate } from 'react-router-dom';
import { type ReactNode, useMemo, useState, useEffect, useId, useRef } from 'react';
import { useChatContext } from '@/contexts/ChatContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { CaretCircleRight, GearSix, SignOut, Sparkle, CaretDown, Buildings, Check, Moon, X, GitDiff, MagicWand } from 'phosphor-react';
import { AnimatePresence, motion } from 'framer-motion';
import { ThemeSwitcher } from './ThemeToggle';
import SettingsModal from './dashboard/SettingsModal';
import { OPEN_SETTINGS_BILLING_EVENT } from './chat/MessageItem';
import { releaseNotes } from '../data/releaseNotes';


/** Milliseconds until next midnight (local time). */
function getTimeUntilNextMidnight(): number {
  const now = new Date();
  const next = new Date(now);
  next.setDate(next.getDate() + 1);
  next.setHours(0, 0, 0, 0);
  return next.getTime() - now.getTime();
}

function formatResetCountdown(ms: number): string {
  if (ms <= 0) return 'Reseta em breve';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) return `Resetam em ${hours}h ${minutes}m`;
  if (minutes > 0) return `Resetam em ${minutes}m`;
  return 'Resetam em menos de 1 min';
}

function SidebarMenuItem({
  label,
  icon,
  active,
  onClick,
  isCollapsed,
  disabled,
}: {
  label: string;
  icon: ReactNode;
  active?: boolean;
  onClick: () => void;
  isCollapsed?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={[
        'group flex items-center gap-2 h-9 w-full rounded-lg text-sm transition-colors',
        'border border-transparent',
        isCollapsed ? 'justify-center px-2' : 'px-3',
        disabled
          ? 'opacity-50 cursor-not-allowed text-[color:var(--foreground-muted)]'
          : active
            ? 'bg-[color:color-mix(in_oklab,var(--card-bg)_78%,var(--nt-primary)_22%)] text-[color:var(--foreground)] border-[color:color-mix(in_oklab,var(--card-border)_70%,var(--nt-primary)_30%)]'
            : 'text-[color:var(--foreground-muted)] hover:text-[color:var(--foreground)] hover:bg-[color:color-mix(in_oklab,var(--card-bg)_88%,var(--nt-primary)_12%)]',
      ].join(' ')}
      title={isCollapsed ? label : undefined}
    >
      <span
        className={[
          'inline-flex size-4 transition-colors flex-shrink-0',
          active
            ? 'text-[color:var(--foreground)]'
            : 'text-[color:var(--foreground-muted)] group-hover:text-[color:var(--foreground)]',
        ].join(' ')}
      >
        {icon}
      </span>
      {!isCollapsed && <span className="font-medium truncate">{label}</span>}
    </button>
  );
}

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const [isExpanded, setIsExpanded] = useState(() => {
    const stored = localStorage.getItem('sidebar-expanded');
    return stored !== 'false';
  });
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsInitialSection, setSettingsInitialSection] = useState<'personal' | 'security' | 'credits-usage' | 'workspace' | 'members' | 'integrations' | 'billing'>('personal');
  const [isWorkspaceSelectorOpen, setIsWorkspaceSelectorOpen] = useState(false);

  useEffect(() => {
    const handler = () => {
      setSettingsInitialSection('billing');
      setIsSettingsOpen(true);
    };
    window.addEventListener(OPEN_SETTINGS_BILLING_EVENT, handler);
    return () => window.removeEventListener(OPEN_SETTINGS_BILLING_EVENT, handler);
  }, []);
  const [isFeaturesModalOpen, setIsFeaturesModalOpen] = useState(false);
  const menuId = useId();
  const menuButtonId = useId();
  const menuRef = useRef<HTMLDivElement>(null);
  const workspaceSelectorRef = useRef<HTMLDivElement>(null);

  const {
    standaloneChats,
    selectedProjectId,
    selectedChatId,
    setSelectedProject,
    selectChat,
    focusChatInput,
  } = useChatContext();

  const {
    workspaces,
    selectedWorkspaceId,
    setSelectedWorkspace,
  } = useWorkspace();

  const isDashboardRoute = pathname.startsWith('/dashboard');
  const isChatRoute = pathname === '/dashboard' || pathname.startsWith('/dashboard/chat');
  const isWorkflowsRoute = pathname.startsWith('/dashboard/workflows');
  const isSkillsRoute = pathname.startsWith('/dashboard/skills');

  const recentChats = useMemo(() => {
    // Only show standalone chats (without projectId) in recent chats
    // SPACES chats (with projectId) should not appear here
    const standaloneChatsOnly = standaloneChats
      .filter((c) => !c.projectId) // Ensure no projectId
      .map((c) => ({
        chat: c,
        projectId: undefined,
      }));

    // Sort by creation date (most recent first)
    standaloneChatsOnly.sort((a, b) => {
      const da = a.chat.createdAt ? new Date(a.chat.createdAt).getTime() : 0;
      const db = b.chat.createdAt ? new Date(b.chat.createdAt).getTime() : 0;
      return db - da;
    });

    return standaloneChatsOnly.slice(0, 7);
  }, [standaloneChats]);

  const goToChat = async (chatId: string, projectId?: string) => {
    // Ensure project is selected before selecting chat (important for spaces chats)
    if (projectId) {
      setSelectedProject(projectId);
      // Use new route format with projectId for spaces chats
      await selectChat(chatId, projectId);
      navigate(`/dashboard/chat/${projectId}/${chatId}`);
    } else {
      // Use old route format for standalone chats
      await selectChat(chatId);
      navigate(`/dashboard/chat/${chatId}`);
    }
    focusChatInput();
  };

  const goToDashboard = (projectId?: string) => {
    if (projectId) setSelectedProject(projectId);
    navigate('/dashboard');
    focusChatInput();
  };

  const handleNewChat = async () => {
    // Clear any selected project to start a new chat without project
    // The project can be selected later or determined from context
    setSelectedProject(null);
    navigate('/dashboard');
    focusChatInput();
  };

  const toggleSidebar = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    localStorage.setItem('sidebar-expanded', String(newState));
  };

  useEffect(() => {
    if (!isUserMenuOpen && !isWorkspaceSelectorOpen) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;

      const clickedElement = target as Element;

      // Check if click is inside user menu container
      const isInsideUserMenu = menuRef.current?.contains(target) ?? false;
      // Check if click is inside workspace selector container
      const isInsideWorkspaceSelector = workspaceSelectorRef.current?.contains(target) ?? false;

      // Check if click is inside any other dropdown/menu (like chat menus)
      // Only check if not clicking inside our own menus
      let isInsideOtherDropdown = false;
      if (!isInsideUserMenu && !isInsideWorkspaceSelector) {
        const closestDropdown = clickedElement.closest('[role="menu"], [role="listbox"], [role="dialog"]');
        if (closestDropdown) {
          // Check if this dropdown is not part of our user menu
          const isOurDropdown =
            menuRef.current?.contains(closestDropdown as Node) ||
            workspaceSelectorRef.current?.contains(closestDropdown as Node);
          isInsideOtherDropdown = !isOurDropdown;
        }
      }

      // Close user menu if clicking outside and not inside workspace selector or other dropdowns
      if (isUserMenuOpen && !isInsideUserMenu && !isInsideWorkspaceSelector && !isInsideOtherDropdown) {
        setIsUserMenuOpen(false);
      }

      // Close workspace selector if clicking outside and not inside user menu or other dropdowns
      if (isWorkspaceSelectorOpen && !isInsideWorkspaceSelector && !isInsideUserMenu && !isInsideOtherDropdown) {
        setIsWorkspaceSelectorOpen(false);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Close workspace selector first if open, then user menu
        if (isWorkspaceSelectorOpen) {
          setIsWorkspaceSelectorOpen(false);
        } else if (isUserMenuOpen) {
          setIsUserMenuOpen(false);
        }
      }
    };

    // Use capture phase to handle clicks before they bubble
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isUserMenuOpen, isWorkspaceSelectorOpen]);

  const displayName = user?.name || user?.email || 'Usuário';
  const initials = (displayName || 'U')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  // Credits for dropdown — daily reset (not cumulative); display max 25 for bar
  const credits = user?.credits ?? 0;
  const creditsMax = 25;
  const creditsPct = Math.max(0, Math.min(100, (Math.min(credits, creditsMax) / creditsMax) * 100));

  // Next reset at midnight (local time); countdown updates every minute
  const [timeUntilReset, setTimeUntilReset] = useState(() => getTimeUntilNextMidnight());
  useEffect(() => {
    const interval = setInterval(() => setTimeUntilReset(getTimeUntilNextMidnight()), 60_000);
    return () => clearInterval(interval);
  }, []);
  const resetLabel = formatResetCountdown(timeUntilReset);

  const sidebarWidth = isExpanded ? 'w-[280px] xl:w-[260px]' : 'w-[72px]';

  // Handle keyboard navigation for features modal
  useEffect(() => {
    if (!isFeaturesModalOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsFeaturesModalOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isFeaturesModalOpen]);

  return (
    <>
      {/* Spacer (keeps layout working with a fixed sidebar) */}
      <div className={`${sidebarWidth} relative max-w-[92vw] z-40 flex-shrink-0 transition-all duration-300`} aria-hidden="true" />

      <aside
        className={[
          'fixed top-0 left-0 bottom-0 h-screen',
          'flex flex-col',
          sidebarWidth,
          'max-w-[92vw]',
          'bg-white dark:bg-[#18181b] border-r theme-border',
          'transition-all duration-300',
          'relative',
        ].join(' ')}
        aria-label="Sidebar"
      >
        {/* Toggle Button - Absolute positioned */}
        <button
          onClick={toggleSidebar}
          className="absolute -right-3 top-3.5 z-20 bg-white dark:bg-[#18181b] theme-border border rounded-full p-0 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-all shadow-lg hover:shadow-xl"
          aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          <CaretCircleRight
            className={`w-6 h-6 text-[color:var(--nt-primary)] transition-transform duration-300 ${isExpanded ? '' : 'rotate-180'}`}
            weight="fill"
          />
        </button>

        {/* Header */}
        <div className={`flex items-center gap-3 px-3 py-3 theme-border border-b ${isExpanded ? 'justify-start' : 'justify-center'}`}>
          {isExpanded ? (
            <Link to={isDashboardRoute ? '/dashboard' : '/'} className="cursor-pointer">
              <img src="/logo-dark.svg" className="h-7 dark:block hidden" alt="Paladino" width={140} height={33} />
              <img src="/logo.svg" className="h-7 dark:hidden block" alt="Paladino" width={140} height={33} />
            </Link>
          ) : (
            <Link to={isDashboardRoute ? '/dashboard' : '/'} className="cursor-pointer">
              <svg className="w-8 h-8" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_978_4087)">
                  <mask id="mask0_978_4087" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="1" y="1" width="64" height="64">
                    <path d="M49 1H17C8.16344 1 1 8.16344 1 17V49C1 57.8366 8.16344 65 17 65H49C57.8366 65 65 57.8366 65 49V17C65 8.16344 57.8366 1 49 1Z" fill="white" />
                  </mask>
                  <g mask="url(#mask0_978_4087)">
                    <path d="M49 1H17C8.16344 1 1 8.16344 1 17V49C1 57.8366 8.16344 65 17 65H49C57.8366 65 65 57.8366 65 49V17C65 8.16344 57.8366 1 49 1Z" fill="#7839EE" />
                    <path d="M1 1H65V65H1V1Z" fill="url(#paint0_linear_978_4087)" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M33 50.1364C34.8693 50.1364 36.6688 49.8371 38.3531 49.2837C36.6356 48.9947 34.8261 48.412 33.0015 47.5551C31.1764 48.4123 29.3664 48.9952 27.6485 49.2843C29.3324 49.8372 31.1312 50.1364 33 50.1364ZM16.7177 38.3572C17.0065 36.6389 17.5893 34.8283 18.4467 33.0025C17.5888 31.1767 17.0055 29.3659 16.7161 27.6473C16.1629 29.3315 15.8636 31.1308 15.8636 33C15.8636 34.8709 16.1635 36.6717 16.7177 38.3572ZM20.3217 36.3473C20.1619 36.8152 20.0255 37.2761 19.9124 37.7281C19.0247 41.2791 19.6759 43.716 20.9812 45.0213C22.2864 46.3265 24.7235 46.9777 28.2744 46.09C28.7267 45.9769 29.1879 45.8404 29.6561 45.6804C27.888 44.5255 26.1504 43.1211 24.5145 41.4852C22.8797 39.8505 21.4763 38.1141 20.3217 36.3473ZM33.0013 44.1912C30.8583 43.0187 28.6768 41.4048 26.6359 39.3639C24.5961 37.3243 22.9829 35.144 21.8107 33.0023C22.9832 30.8591 24.5971 28.6775 26.638 26.6365C28.6783 24.5963 30.8591 22.9828 33.0013 21.8104C35.1435 22.9828 37.3239 24.5961 39.3637 26.636C41.4049 28.6771 43.0189 30.8589 44.1915 33.0023C43.0191 35.1443 41.4059 37.3245 39.366 39.3644C37.3253 41.4051 35.1441 43.0187 33.0013 44.1912ZM45.6803 36.3475C44.5257 38.1144 43.1221 39.8509 41.4873 41.4857C39.8517 43.1213 38.1144 44.5253 36.3467 45.6803C36.8145 45.8401 37.2756 45.9765 37.7275 46.0895C41.2784 46.9772 43.7153 46.326 45.0207 45.0208C46.3259 43.7155 46.9771 41.2785 46.0893 37.7276C45.9764 37.2759 45.84 36.8151 45.6803 36.3475ZM47.5553 33.0025C48.4127 31.178 48.9956 29.3685 49.2851 27.6512C49.8375 29.3341 50.1364 31.1321 50.1364 33C50.1364 34.8695 49.8369 36.6691 49.2836 38.3533C48.9945 36.6363 48.4121 34.8269 47.5553 33.0025ZM38.3547 16.7168C36.6367 17.006 34.8265 17.5889 33.0015 18.4464C31.1759 17.5887 29.3653 17.0055 27.6469 16.7163C29.3312 16.1629 31.1307 15.8636 33 15.8636C34.87 15.8636 36.67 16.1632 38.3547 16.7168ZM47.0315 18.7481C43.4213 15.1933 38.4668 13 33 13C27.4993 13 22.5173 15.2207 18.9016 18.8143L18.8577 18.8579L18.814 18.9019C15.2205 22.5176 13 27.4995 13 33C13 44.0457 21.9543 53 33 53C44.0457 53 53 44.0457 53 33C53 27.5352 50.8083 22.5823 47.2557 18.9724C47.2191 18.934 47.1819 18.896 47.1441 18.8583C47.1069 18.8211 47.0693 18.7844 47.0315 18.7481ZM29.6568 20.3209C29.1876 20.1605 28.7253 20.0236 28.2723 19.9104C24.7212 19.0227 22.2843 19.6739 20.9789 20.9792C19.6737 22.2844 19.0225 24.7213 19.9103 28.2724C20.0236 28.7257 20.1605 29.1881 20.3211 29.6575C21.4761 27.8892 22.8807 26.1513 24.5167 24.5152C26.1521 22.8797 27.8892 21.4757 29.6568 20.3209ZM45.6809 29.6573C44.5259 27.8889 43.1213 26.1509 41.4851 24.5147C39.85 22.8796 38.1132 21.4759 36.346 20.3212C36.8149 20.1609 37.2768 20.0241 37.7296 19.9109C41.2805 19.0232 43.7176 19.6744 45.0228 20.9796C46.3281 22.2849 46.9793 24.7219 46.0916 28.2728C45.9783 28.726 45.8413 29.1881 45.6809 29.6573Z" fill="url(#paint1_linear_978_4087)" />
                  </g>
                  <path d="M48.9987 2.33398H16.9987C8.89852 2.33398 2.33203 8.90047 2.33203 17.0007V49.0006C2.33203 57.1008 8.89852 63.6673 16.9987 63.6673H48.9987C57.0989 63.6673 63.6654 57.1008 63.6654 49.0006V17.0007C63.6654 8.90047 57.0989 2.33398 48.9987 2.33398Z" stroke="url(#paint2_linear_978_4087)" strokeWidth="2" />
                </g>
                <defs>
                  <linearGradient id="paint0_linear_978_4087" x1="33" y1="1" x2="35.6667" y2="65" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" stopOpacity="0" />
                    <stop offset="1" stopColor="white" stopOpacity="0.12" />
                  </linearGradient>
                  <linearGradient id="paint1_linear_978_4087" x1="33" y1="13" x2="33" y2="53" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" stopOpacity="0.8" />
                    <stop offset="1" stopColor="white" stopOpacity="0.5" />
                  </linearGradient>
                  <linearGradient id="paint2_linear_978_4087" x1="32.9987" y1="1.00065" x2="32.9987" y2="65.0007" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" stopOpacity="0.12" />
                    <stop offset="1" stopColor="white" stopOpacity="0" />
                  </linearGradient>
                  <clipPath id="clip0_978_4087">
                    <rect width="64" height="64" fill="white" transform="translate(1 1)" />
                  </clipPath>
                </defs>
              </svg>
            </Link>
          )}
        </div>

        <div className="grow overflow-y-auto">
          {/* Primary CTA */}
          <div className="px-3 py-3">
            <button
              type="button"
              onClick={() => void handleNewChat()}
              className={`inline-flex items-center justify-center w-full h-8 rounded-lg text-xs font-medium transition-all cursor-pointer disabled:pointer-events-none bg-[color:var(--nt-primary)] text-white fill-white shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom),inset_0_0.125rem_0_0_var(--nt-primary-inset-top)] hover:brightness-95 hover:shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom-hover),inset_0_0.125rem_0_0_var(--nt-primary-inset-top-hover)] ${isExpanded ? 'px-4 !gap-0' : 'px-2'}`}
              title={!isExpanded ? t('sidebar.newChat') : undefined}
            >
              <svg className="inline-flex size-4 fill-inherit" width="16" height="16" viewBox="0 0 16 16">
                <path d="M8.663 3.33l-.001 4h4.001v1.333H8.662l.001 4H7.33v-4h-4V7.33h4v-4z"></path>
              </svg>
              {isExpanded && <span className="ml-2">{t('sidebar.newChat')}</span>}
            </button>
          </div>

          {/* MAIN MENU */}
          <div className="px-3 py-4">
            <div className="space-y-1">
              <SidebarMenuItem
                label={t('sidebar.chat')}
                active={isChatRoute}
                onClick={() => goToDashboard(selectedProjectId || undefined)}
                isCollapsed={!isExpanded}
                icon={
                  <svg className="size-4" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M13.136 5.582l-.164.377c-.12.276-.502.276-.622 0l-.164-.377a2.91 2.91 0 0 0-1.479-1.501l-.506-.225c-.274-.122-.274-.52 0-.642l.478-.213a2.91 2.91 0 0 0 1.501-1.551l.169-.408c.118-.284.51-.284.628 0l.169.408a2.91 2.91 0 0 0 1.501 1.551l.478.213c.274.122.274.52 0 .642l-.506.225a2.91 2.91 0 0 0-1.479 1.501zM5.993 2.163H8.66v1.333H5.993a4 4 0 0 0-4 4c0 2.407 1.641 3.977 5.333 5.653v-1.653H8.66a4 4 0 0 0 4-4h1.333c0 2.946-2.388 5.333-5.333 5.333v2.333c-3.333-1.333-8-3.333-8-7.667 0-2.946 2.388-5.333 5.333-5.333z"></path>
                  </svg>
                }
              />
              <SidebarMenuItem
                label={t('sidebar.workflows')}
                active={isWorkflowsRoute}
                onClick={() => navigate('/dashboard/workflows')}
                isCollapsed={!isExpanded}
                icon={<GitDiff className="size-4" />}
              />
              <SidebarMenuItem
                label={t('sidebar.skills')}
                active={isSkillsRoute}
                onClick={() => navigate('/dashboard/skills')}
                isCollapsed={!isExpanded}
                icon={<MagicWand className="size-4" />}
              />
            </div>
          </div>


          {/* RECENT CHATS */}
          {isExpanded && (
            <div className="px-3 py-4">
              <div className="mb-2 pl-2 text-xs tracking-wide theme-text-muted">CONVERSAS RECENTES</div>
              {recentChats.length === 0 ? (
                <div className="px-3 py-2 text-sm theme-text-muted">Nenhuma conversa recente</div>
              ) : (
                <div className="flex flex-col gap-1">
                  {recentChats.map(({ chat, projectId }) => {
                    const active = selectedChatId === chat.id;
                    return (
                      <button
                        key={chat.id}
                        type="button"
                        onClick={() => void goToChat(chat.id, projectId)}
                        className={[
                          'truncate px-3 py-2 rounded-lg text-sm text-left transition-colors border border-transparent',
                          active
                            ? 'bg-[color:color-mix(in_oklab,var(--card-bg)_78%,var(--nt-primary)_22%)] text-[color:var(--foreground)] border-[color:color-mix(in_oklab,var(--card-border)_70%,var(--nt-primary)_30%)]'
                            : 'text-[color:var(--foreground-muted)] hover:text-[color:var(--nt-primary)] hover:bg-[color:color-mix(in_oklab,var(--card-bg)_88%,var(--nt-primary)_12%)]',
                        ].join(' ')}
                        title={chat.title}
                      >
                        {chat.title}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Menu at Bottom */}
        <div className="shrink-0 border-t theme-border p-3">
          <div className={`flex items-center ${isExpanded ? 'gap-1' : 'justify-center'}`}>
            <div className="relative flex-1 min-w-0" ref={menuRef}>
              <button
                type="button"
                className={`flex items-center gap-2 w-full rounded-xl px-2 py-1.5 theme-text hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors ${isExpanded ? 'justify-start' : 'justify-center'}`}
                id={menuButtonId}
                aria-haspopup="menu"
                aria-expanded={isUserMenuOpen}
                aria-controls={menuId}
                onClick={() => setIsUserMenuOpen((v) => !v)}
                title={!isExpanded ? displayName : undefined}
              >
                <span className="relative inline-flex h-9 w-9 items-center border theme-border justify-center rounded-full bg-white dark:bg-[#18181b] shrink-0">
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt={displayName}
                      className="h-full w-full rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-[11px] font-semibold tracking-wide text-[color:var(--foreground)]">
                      {initials || 'U'}
                    </span>
                  )}
                </span>
  
                {isExpanded && (
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate text-left">
                      {displayName}
                    </div>
                    <div className="text-xs theme-text-muted truncate text-left mt-0.5">
                      {workspaces.find(w => w.id === selectedWorkspaceId)?.name || 'Selecionar Equipe'}
                    </div>
                  </div>
                )}
              </button>

            <AnimatePresence>
              {isUserMenuOpen && (
                <motion.div
                  id={menuId}
                  role="menu"
                  aria-labelledby={menuButtonId}
                  tabIndex={0}
                  initial={{ opacity: 0, scale: 0.96, y: 6 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96, y: 6 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                  className="absolute bottom-full left-0 mb-2 z-[100] w-[15.5rem] origin-bottom-left overflow-hidden rounded-3xl border theme-border bg-white dark:bg-[#18181b] shadow-lg shadow-black/10 outline-0 dark:shadow-black/30"
                >
                  {/* User Info Section */}
                  <div className="p-4 border-b theme-border">
                    <div className="mb-3">
                      <div className="text-sm font-semibold theme-text truncate mb-0.5">{displayName}</div>
                      {user?.email && (
                        <div className="text-xs theme-text-muted truncate">{user.email}</div>
                      )}
                    </div>

                    {/* Credits Card — daily reset, not cumulative */}
                    <div className="p-3 rounded-xl border theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_50%,transparent)] mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium theme-text-muted">Créditos</span>
                        <span className="text-xs font-semibold theme-text">{credits}/{creditsMax}</span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-[color:color-mix(in_oklab,var(--card-border)_40%,transparent)] overflow-hidden">
                        <motion.div
                          className="h-full rounded-full bg-[color:var(--nt-primary)]"
                          initial={{ width: 0 }}
                          animate={{ width: `${creditsPct}%` }}
                          transition={{ duration: 0.5, ease: 'easeOut' }}
                        />
                      </div>
                      <div className="mt-1.5 text-[10px] theme-text-muted" title="Créditos resetam todo dia à meia-noite (horário local)">
                        {resetLabel}
                      </div>
                    </div>

                    {/* Workspace Selector */}
                    <div className="relative" ref={workspaceSelectorRef}>
                      <button
                        type="button"
                        onClick={() => setIsWorkspaceSelectorOpen(!isWorkspaceSelectorOpen)}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_50%,transparent)] hover:bg-[color:color-mix(in_oklab,var(--card-bg)_70%,transparent)] transition-colors"
                      >
                        {(() => {
                          const ws = workspaces.find((w) => w.id === selectedWorkspaceId);
                          return ws?.avatarUrl ? (
                            <img
                              src={ws.avatarUrl}
                              alt=""
                              className="size-8 rounded-lg object-cover shrink-0 border theme-border"
                            />
                          ) : (
                            <Buildings className="size-4 text-[color:var(--nt-primary)] shrink-0" weight="fill" />
                          );
                        })()}
                        <div className="flex-1 min-w-0 text-left">
                          <div className="text-xs font-semibold theme-text truncate">
                            {workspaces.find((w) => w.id === selectedWorkspaceId)?.name || 'Selecionar Equipe'}
                          </div>
                          <div className="text-[10px] theme-text-muted truncate">
                            {workspaces.find((w) => w.id === selectedWorkspaceId)?.ownerUserId === user?.id ? 'Proprietário' : 'Membro'}
                          </div>
                        </div>
                        <CaretDown
                          className={`size-3 theme-text-muted transition-transform shrink-0 ${isWorkspaceSelectorOpen ? 'rotate-180' : ''}`}
                        />
                      </button>

                      <AnimatePresence>
                        {isWorkspaceSelectorOpen && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.96, y: -6 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.96, y: -6 }}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                            className="absolute top-full left-0 mt-2 z-[100] w-full origin-top-left overflow-hidden rounded-xl border theme-border bg-white dark:bg-[#18181b] shadow-lg shadow-black/10 outline-0 dark:shadow-black/30"
                          >
                            <div className="p-2">
                              <div className="flex items-center justify-between px-2 py-1.5 mb-1">
                                <div className="text-[10px] font-semibold theme-text-muted uppercase tracking-wide">
                                  Equipes
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsWorkspaceSelectorOpen(false);
                                    navigate('/workspaces/create');
                                  }}
                                  className="w-5 h-5 rounded-lg hover:bg-[color:color-mix(in_oklab,var(--card-bg)_88%,var(--nt-primary)_12%)] text-[color:var(--foreground-muted)] hover:text-[color:var(--foreground)] flex items-center justify-center transition-colors"
                                  title="Criar workspace"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                </button>
                              </div>
                              <div className="space-y-0.5">
                                {workspaces.length === 0 ? (
                                  <div className="px-2 py-2 text-xs theme-text-muted text-center">
                                    Nenhum workspace encontrado
                                  </div>
                                ) : (
                                  workspaces.map((workspace) => (
                                    <button
                                      key={workspace.id}
                                      type="button"
                                      onClick={async () => {
                                        const previousWorkspaceId = selectedWorkspaceId;
                                        await setSelectedWorkspace(workspace.id);
                                        setIsWorkspaceSelectorOpen(false);

                                        // If workspace changed and we're on a chat page, navigate to dashboard
                                        if (previousWorkspaceId !== workspace.id && pathname.includes('/dashboard/chat/')) {
                                          navigate('/dashboard');
                                        }
                                      }}
                                      className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-xs transition-colors text-left ${selectedWorkspaceId === workspace.id
                                        ? 'bg-[color:color-mix(in_oklab,var(--card-bg)_78%,var(--nt-primary)_22%)] text-[color:var(--foreground)]'
                                        : 'theme-text-muted hover:bg-[color:color-mix(in_oklab,var(--card-bg)_88%,var(--nt-primary)_12%)] hover:text-[color:var(--foreground)]'
                                        }`}
                                    >
                                      {workspace.avatarUrl ? (
                                        <img
                                          src={workspace.avatarUrl}
                                          alt=""
                                          className="size-8 rounded-lg object-cover shrink-0 border theme-border"
                                        />
                                      ) : (
                                        <Buildings className="size-3.5 shrink-0" weight={selectedWorkspaceId === workspace.id ? 'fill' : 'regular'} />
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <div className="truncate font-medium">{workspace.name}</div>
                                        <div className="text-[10px] opacity-60 truncate">
                                          {workspace.ownerUserId === user?.id ? 'Proprietário' : 'Membro'}
                                        </div>
                                      </div>
                                      {selectedWorkspaceId === workspace.id && (
                                        <Check className="size-3.5 text-[color:var(--nt-primary)] shrink-0" weight="bold" />
                                      )}
                                    </button>
                                  ))
                                )}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Actions Section */}
                  <div className="p-2">
                    <button
                      type="button"
                      role="menuitem"
                      className="flex h-11 w-full items-center gap-3 rounded-xl px-3 text-sm font-medium theme-text-muted transition-colors hover:bg-[color:color-mix(in_oklab,var(--card-bg)_88%,var(--nt-primary)_12%)] hover:text-[color:var(--foreground)]"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        setSettingsInitialSection('personal');
                        setIsSettingsOpen(true);
                      }}
                    >
                      <GearSix className="size-5 shrink-0" weight="regular" />
                      <span>Configurações</span>
                    </button>
                  </div>

                  {/* Preferences Section */}
                  <div className="p-2 pt-0">
                    <div className="flex items-center justify-between w-full h-11 px-3 rounded-xl hover:bg-[color:color-mix(in_oklab,var(--card-bg)_88%,var(--nt-primary)_12%)] transition-colors">
                      <div className="flex items-center gap-3">
                        <Moon className="size-5 theme-text-muted shrink-0" weight="regular" />
                        <span className="text-sm font-medium theme-text-muted">Tema</span>
                      </div>
                      <ThemeSwitcher />
                    </div>
                  </div>

                  {/* Logout Section */}
                  <div className="border-t theme-border p-2">
                    <button
                      type="button"
                      role="menuitem"
                      className="flex h-11 w-full items-center gap-3 rounded-xl px-3 text-sm font-medium text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        logout();
                      }}
                    >
                      <SignOut className="size-5 shrink-0" weight="regular" />
                      <span>Sair</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            </div>

            {/* What's New Minimimalist Icon */}
            {isExpanded && (
              <button
                type="button"
                onClick={() => setIsFeaturesModalOpen(true)}
                className="relative p-2 shrink-0 rounded-xl text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-gray-100 dark:hover:bg-zinc-800/50 transition-colors"
                aria-label="Ver novidades e atualizações"
                title="Novidades"
              >
                {new Date(releaseNotes[0]?.isNewUntil) > new Date() && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-blue-500 ring-2 ring-white dark:ring-[#18181b]" />
                )}
                <Sparkle className="size-[20px]" weight="regular" />
              </button>
            )}
          </div>
        </div>

      </aside>


      <SettingsModal
        isOpen={isSettingsOpen}
        initialSectionId={settingsInitialSection}
        onClose={() => {
          setIsSettingsOpen(false);
          setSettingsInitialSection('personal');
        }}
      />

      {/* Features Modal - Scrollable Feed */}
      <AnimatePresence>
        {isFeaturesModalOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsFeaturesModalOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: 12 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 py-8 pointer-events-none"
            >
              <div
                className="relative w-full max-w-2xl max-h-full rounded-2xl border theme-border shadow-xl overflow-hidden flex flex-col pointer-events-auto bg-[color:var(--card-bg)] dark:bg-zinc-900 min-w-0"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="features-modal-title"
              >
                {/* Header (Sticky) */}
                <div className="sticky top-0 z-20 shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/5 bg-white/90 dark:bg-[#121212]/90 backdrop-blur-md">
                  <h2 id="features-modal-title" className="text-[15px] font-semibold theme-text text-zinc-900 dark:text-zinc-100">
                    What's New
                  </h2>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setIsFeaturesModalOpen(false)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-[color:var(--foreground)] transition-colors focus:ring-2 focus:ring-[color:var(--nt-primary)] outline-none"
                      aria-label="Fechar modal"
                    >
                      <X className="size-4" weight="bold" />
                    </button>
                  </div>
                </div>

                {/* Content — scrollable area */}
                <div className="relative flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-zinc-300 dark:scrollbar-thumb-zinc-700 scrollbar-track-transparent bg-zinc-50 dark:bg-black/20">
                  <div className="p-4 md:p-5 flex flex-col">
                    {releaseNotes.map((note) => (
                      <div key={note.id} className="flex flex-col p-4 md:p-5 mb-6 last:mb-0 bg-white dark:bg-zinc-900 rounded-2xl border border-black/5 dark:border-white/5">
                        {/* Hero Image */}
                        <div className="w-full rounded-xl overflow-hidden mb-6 bg-zinc-100 dark:bg-zinc-950">
                          <img
                            src={note.heroImage}
                            alt=""
                            className="w-full aspect-[16/10] object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23111" width="100" height="100"/%3E%3Ctext fill="%23666" font-family="sans-serif" font-size="14" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3EImagem indispon%C3%ADvel%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        </div>

                        <div className="mb-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">{note.date}</span>
                        </div>

                        <h3 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-4 leading-tight tracking-tight">
                          {note.title}
                        </h3>

                        <div className="prose prose-zinc prose-sm dark:prose-invert max-w-none text-zinc-700 dark:text-zinc-300 leading-relaxed">
                          <div className="whitespace-pre-wrap">{note.content}</div>
                        </div>

                        {note.images && note.images.length > 0 && (
                          <div className="mt-8 space-y-6">
                            {note.images.map((img, i) => (
                              <div key={i} className="w-full rounded-xl overflow-hidden bg-zinc-100 dark:bg-zinc-950">
                                <img src={img} alt="" className="w-full object-cover" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
